import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Search, 
  Filter, 
  MoreHorizontal, 
  Upload,
  Download,
  Eye,
  Edit,
  Trash2,
  FileQuestion,
  Settings,
  EyeOff
} from 'lucide-react';
import { Question } from './types';
import { BulkActionsDialog } from './BulkActionsDialog';

interface QuestionsTableProps {
  questions: Question[];
  loading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  typeFilter: string;
  setTypeFilter: (filter: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalQuestions: number;
  questionsPerPage: number;
  onImportClick: () => void;
  onDeleteClick: (questionId: string) => void;
  onEditClick: (question: Question) => void;
  onViewClick: (question: Question) => void;
  onBulkAction: (action: string, questionIds: string[], value?: string) => void;
  onToggleVisibility?: (question: Question) => void;
}

export const QuestionsTable = ({
  questions,
  loading,
  searchTerm,
  setSearchTerm,
  typeFilter,
  setTypeFilter,
  currentPage,
  setCurrentPage,
  totalQuestions,
  questionsPerPage,
  onImportClick,
  onDeleteClick,
  onEditClick,
  onViewClick,
  onBulkAction,
  onToggleVisibility,
}: QuestionsTableProps) => {
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'multiple_choice': return 'default';
      case 'true_false': return 'secondary';
      case 'essay': return 'outline';
      default: return 'default';
    }
  };

  const totalPages = Math.ceil(totalQuestions / questionsPerPage);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedQuestions(questions.map(q => q.id));
    } else {
      setSelectedQuestions([]);
    }
  };

  const handleSelectQuestion = (questionId: string, checked: boolean) => {
    if (checked) {
      setSelectedQuestions([...selectedQuestions, questionId]);
    } else {
      setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
    }
  };

  const handleBulkAction = async (action: string, value?: string) => {
    setBulkActionLoading(true);
    try {
      await onBulkAction(action, selectedQuestions, value);
      setSelectedQuestions([]);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const allSelected = questions.length > 0 && selectedQuestions.length === questions.length;
  const someSelected = selectedQuestions.length > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Questions ({totalQuestions})</CardTitle>
          <div className="flex gap-2">
            {someSelected && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setBulkActionsOpen(true)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Bulk Actions ({selectedQuestions.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onImportClick}>
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search questions by text or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
              <SelectItem value="true_false">True/False</SelectItem>
              <SelectItem value="essay">Essay</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all questions"
                  />
                </TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Question</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Correct Answer</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Loading questions...
                  </TableCell>
                </TableRow>
              ) : questions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No questions found
                  </TableCell>
                </TableRow>
              ) : (
                questions.map((question) => (
                  <TableRow key={question.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedQuestions.includes(question.id)}
                        onCheckedChange={(checked) => 
                          handleSelectQuestion(question.id, checked as boolean)
                        }
                        aria-label={`Select question ${question.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-sm bg-muted px-2 py-1 rounded max-w-24 truncate" title={question.custom_id || question.id}>
                        {question.custom_id || question.id.substring(0, 8)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-3">
                        <FileQuestion className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <div className="max-w-md">
                          <div className="font-medium line-clamp-2">
                            {question.question_text}
                          </div>
                          {question.explanation && (
                            <div className="text-sm text-muted-foreground mt-1">
                              Has explanation
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getTypeColor(question.question_type || 'unknown')}>
                        {(question.question_type || 'unknown').replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {question.is_active ? (
                          <Badge variant="default" className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            Visible
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <EyeOff className="h-3 w-3" />
                            Hidden
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {Array.isArray(question.correct_answer) ? question.correct_answer.join(', ') : question.correct_answer}
                      </code>
                    </TableCell>
                    <TableCell>{formatDate(question.created_at)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onViewClick(question)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onEditClick(question)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Question
                          </DropdownMenuItem>
                          {onToggleVisibility && (
                            <DropdownMenuItem onClick={() => onToggleVisibility(question)}>
                              {question.is_active ? (
                                <>
                                  <EyeOff className="h-4 w-4 mr-2" />
                                  Hide from Users
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Show to Users
                                </>
                              )}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => onDeleteClick(question.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * questionsPerPage + 1} to{' '}
              {Math.min(currentPage * questionsPerPage, totalQuestions)} of {totalQuestions} questions
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <BulkActionsDialog
          open={bulkActionsOpen}
          onOpenChange={setBulkActionsOpen}
          selectedCount={selectedQuestions.length}
          onBulkAction={handleBulkAction}
          loading={bulkActionLoading}
        />
      </CardContent>
    </Card>
  );
};